import {
  BookText,
  Building2,
  ListChecks,
  Settings2,
  SlidersHorizontal,
  Sparkles,
  Tag,
  Search,
} from 'lucide-react'
import type { ComponentType } from 'react'
import {
  Applicants,
  Apps,
  Browse,
  Dash,
  Export,
  Home,
  Lock,
  Matrix,
  type IconProps,
} from '@/components/icons'
import type { Role } from '@/lib/mobility'

/**
 * The four portals + their nav. A principal's roles decide which portals they
 * can reach (the shell's portal switcher); the active portal decides which nav
 * items render. EMPLOYEE is implicit for every active roster, so the Employee
 * portal is always available. Portal agents ADD nav items to their portal's
 * array here (and pages under the matching route) without touching the shell.
 *
 * Icons come from the design's ported set (components/icons.tsx); lucide is
 * only used where the design set has no glyph (Settings).
 */
export type PortalId = 'employee' | 'hr' | 'recruiter' | 'admin'

/** Sidebar count pills the shell knows how to resolve (employee portal only). */
export type NavCountKey = 'myApplications'

export interface NavItem {
  to: string
  label: string
  icon: ComponentType<IconProps>
  /** Match child routes too (e.g. /recruiter/req/123 highlights the Requisitions item). */
  end?: boolean
  /** Optional count pill, resolved by the shell (undefined → no pill). */
  countKey?: NavCountKey
  /**
   * What the count pill COUNTS, read out beside the bare number (sr-only + a
   * hover title). A pill that shows `5` next to "Browse roles" while the board
   * says "6 open roles" is unreadable without it.
   */
  countLabel?: string
  /**
   * Optional sidebar section this item sits under. The shell prints the label
   * once, when it CHANGES — so the nav stays a flat array and every consumer
   * that iterates it is untouched. A portal whose items carry no section
   * renders exactly as before, under the portal's own `groupLabel`.
   *
   * Items sharing a section MUST be adjacent; the shell prints on change, so a
   * section that reappears later would print twice.
   */
  section?: string
}

/**
 * Sections the sidebar renders as a COLLAPSIBLE group, closed on arrival.
 *
 * The admin sidebar had grown to fifteen items across five always-open
 * sections, which is longer than the viewport — so the items an admin opens
 * daily (Requisitions, Applications) shared the screen with the ones they open
 * a handful of times a year, and the daily ones were not obviously the daily
 * ones. Collapsing the rarely-used run keeps every item reachable while making
 * the short list the default view.
 *
 * A section named here still prints its label; the difference is that the label
 * is a toggle and its items start hidden. Everything else about the flat `nav`
 * array is unchanged.
 */
export const COLLAPSIBLE_SECTIONS: readonly string[] = ['Advance']

/** Is this section one the sidebar collapses by default? */
export function isCollapsibleSection(section: string | undefined): boolean {
  return section !== undefined && COLLAPSIBLE_SECTIONS.includes(section)
}

export interface Portal {
  id: PortalId
  label: string
  /** Sidebar group label (uppercase heading above the nav items). */
  groupLabel: string
  /** Default landing route for this portal. */
  home: string
  /**
   * The roles that unlock this portal — holding ANY of them shows it in the
   * switcher (EMPLOYEE is implicit for everyone). Wave US (US-2/US-D3): the
   * recruiter portal also admits HR_PARTNER — any staff role can be a
   * requisition's assignee, and the backend scopes their lists server-side.
   */
  requires: readonly Role[]
  nav: NavItem[]
}

export const PORTALS: Portal[] = [
  {
    id: 'employee',
    label: 'Employee',
    groupLabel: 'My workspace',
    home: '/home',
    requires: ['EMPLOYEE'],
    nav: [
      { to: '/home', label: 'Home', icon: Home },
      // Above the full board on purpose: the ranked list is the point of the
      // recommender, and browsing everything is the fallback, not the headline.
      { to: '/recommendations', label: 'Recommended for you', icon: Sparkles },
      // Above "Browse roles": asking in your own words is the headline, and
      // browsing the literal board is the fallback.
      { to: '/discover', label: 'Discover', icon: Sparkles },
      { to: '/jobs', label: 'Browse roles', icon: Browse },
      {
        to: '/applications',
        label: 'My applications',
        icon: Apps,
        countKey: 'myApplications',
        countLabel: 'applications',
      },
    ],
  },
  {
    id: 'hr',
    label: 'HR Partner',
    groupLabel: 'HR Partner',
    home: '/hr',
    requires: ['HR_PARTNER'],
    nav: [
      // FB-11: management's wording (M-8) is "IJP base" — the same words the
      // upload panel already used, and the same words the admin portal uses.
      { to: '/hr', label: 'IJP base', icon: Applicants, end: true },
      { to: '/hr/uploads', label: 'Upload history', icon: Export },
    ],
  },
  {
    id: 'recruiter',
    label: 'Recruiter',
    groupLabel: 'Talent acquisition',
    home: '/recruiter',
    requires: ['HR_PARTNER', 'HR_ADMIN'],
    nav: [
      { to: '/recruiter', label: 'Requisitions', icon: Dash, end: true },
      { to: '/recruiter/applications', label: 'Applications', icon: Applicants },
    ],
  },
  {
    id: 'admin',
    label: 'HR Admin',
    groupLabel: 'Administration',
    home: '/admin',
    requires: ['HR_ADMIN'],
    // SECTIONED (user's choice of option 1). Thirteen peers with no hierarchy
    // buried the two items an admin opens daily under eleven they open monthly.
    // Nothing is hidden and nothing moves off the sidebar — the labels just say
    // what kind of thing each run of items is. Order changed to put like with
    // like, which is what makes the labels true.
    nav: [
      // What the admin actually does day to day, first. Entities joins them:
      // it is the list every other screen's pickers resolve against, and it is
      // opened alongside requisition work rather than as configuration.
      { to: '/admin', label: 'Requisitions', icon: Dash, end: true, section: 'Work' },
      { to: '/admin/applications', label: 'Applications', icon: Apps, section: 'Work' },
      { to: '/admin/entities', label: 'Entities', icon: Browse, section: 'Work' },
      // FB-11: the eligibility allow-list, under the ONE name it has everywhere.
      { to: '/admin/ijp-base', label: 'IJP base', icon: ListChecks, section: 'People' },
      // The screen that decides WHO SEES WHAT. It sat alone under an
      // ELIGIBILITY heading, which named the CONSEQUENCE of the two screens
      // rather than either screen's subject — and a heading over one link is
      // just a taller link. Both of these are statements about PEOPLE (which
      // grade someone is, and who is on the allow-list), so they read as one
      // group, and the sidebar loses a heading without losing a destination.
      { to: '/admin/grades', label: 'Grade matrix', icon: Matrix, section: 'People' },
      // WAVE M1/M2/F3 — the vocabulary a requisition is AUTHORED from.
      //
      // THE JOB-ROLE CATALOGUE IS "Roles" AND THE ACCESS GRANTS ARE "Access
      // roles" — the qualifier moved, it did not disappear. These are two
      // unrelated screens and one bare word cannot name both, so whichever
      // keeps the plain noun, the OTHER has to carry the distinguishing word.
      // This is the job-role master (what a requisition is authored from) and it
      // is the one people open daily, so it takes the plain name; the ACCESS
      // grants live under Advance and are configured rarely.
      { to: '/admin/departments', label: 'Departments', icon: Building2, section: 'Masters' },
      { to: '/admin/role-master', label: 'Roles', icon: BookText, section: 'Masters' },
      // FB-6: the centrally managed, DISPLAY-ONLY badge catalogue — catalogue
      // vocabulary too, even though it never affects eligibility or ranking.
      { to: '/admin/badges', label: 'Job badges', icon: Tag, section: 'Masters' },
      // ── ADVANCE — collapsed by default (see COLLAPSIBLE_SECTIONS) ──────────
      //
      // The tuning and operations surfaces: real screens, rarely opened, and
      // between them they were half the sidebar's height. Roster and Skill
      // master are bulk-refreshed data; Search operations and Match policy tune
      // the recommender; Roles, Settings and the application lifecycle are
      // estate configuration. None is a daily destination, and all are one
      // click away.
      { to: '/admin/roster', label: 'Roster', icon: Applicants, section: 'Advance' },
      { to: '/admin/skill-master', label: 'Skill master', icon: Sparkles, section: 'Advance' },
      { to: '/admin/recommender', label: 'Search operations', icon: Search, section: 'Advance' },
      {
        to: '/admin/match-policy',
        label: 'Match policy',
        icon: SlidersHorizontal,
        section: 'Advance',
      },
      // WHO MAY DO WHAT — grants, not job descriptions. See the note above the
      // job-role master for why exactly one of the two carries a qualifier.
      { to: '/admin/roles', label: 'Access roles', icon: Lock, section: 'Advance' },
      // WAVE M6 — the configurable APPLICATION lifecycle. It governs how an
      // application MOVES, not what a posting is for, so it never belonged in
      // Masters; and it is set up once and then read by every recruiter's
      // transition menu, which is exactly the "configure rarely, rely on daily"
      // shape the rest of this group has. It was the sole occupant of a SYSTEM
      // heading, and a section heading over one link is just a taller link.
      {
        to: '/admin/statuses',
        label: 'Application statuses',
        icon: ListChecks,
        section: 'Advance',
      },
      { to: '/admin/settings', label: 'Settings', icon: Settings2, section: 'Advance' },
      ...((Boolean(import.meta.env.DEV) || import.meta.env.VITE_AXIS_DEMO_ENABLED === 'true')
        ? [
            {
              to: '/demo/axis-demo',
              label: 'AXIS live demo',
              icon: Sparkles,
              section: 'Advance',
            },
          ]
        : []),
    ],
  },
]

/** The portals a principal with `roles` can reach (Employee always included). */
export function portalsForRoles(roles: readonly Role[]): Portal[] {
  return PORTALS.filter(
    (p) => p.requires.includes('EMPLOYEE') || p.requires.some((r) => roles.includes(r)),
  )
}

/** The portal that owns a given pathname (by route prefix), defaulting to Employee. */
export function portalForPath(pathname: string): Portal {
  if (pathname.startsWith('/admin')) return PORTALS[3]
  if (pathname.startsWith('/recruiter')) return PORTALS[2]
  if (pathname.startsWith('/hr')) return PORTALS[1]
  return PORTALS[0] // /home, /jobs, /applications + fallback
}
